import { buffer, area } from '@turf/turf'
import type { Feature, Polygon, MultiPolygon } from 'geojson'

/** Loose GeoJSON polygon shape as stored on Land.geo_polygon. */
export interface GeoJsonPolygonLike {
  type: string
  coordinates: number[][][] | number[][][][]
}

export interface ErodePolygonResult {
  /** Eroded polygon (null when the erosion is empty or input invalid). */
  polygon: GeoJsonPolygonLike | null
  /** Area of the eroded polygon in m2 (0 when empty). */
  areaM2: number
}

/**
 * Uniform setback erosion: shrinks a parcel polygon inward by `setbackM`
 * metres (turf negative buffer) and returns the remaining footprint + area.
 *
 * This is the shared envelope-geometry routine (browser preview + API). It
 * covers the uniform small-setback case only; the one-sided large-setback
 * band and length/depth caps are pipeline/endpoint territory.
 *
 * An empty erosion returns {polygon: null, areaM2: 0} - per the data
 * pipeline, that means "no compliant building placement on this parcel
 * alone", not "fully exploited".
 */
export const erodePolygonByMeters = (
  polygon: GeoJsonPolygonLike,
  setbackM: number
): ErodePolygonResult => {
  try {
    if (
      !polygon ||
      (polygon.type !== 'Polygon' && polygon.type !== 'MultiPolygon') ||
      !Array.isArray(polygon.coordinates) ||
      polygon.coordinates.length === 0
    ) {
      return { polygon: null, areaM2: 0 }
    }

    const feature = {
      type: 'Feature',
      properties: {},
      geometry: polygon,
    } as Feature<Polygon | MultiPolygon>

    if (setbackM === 0) {
      return { polygon, areaM2: Math.round(area(feature)) }
    }

    const eroded = buffer(feature, -Math.abs(setbackM), { units: 'meters' })
    if (!eroded || !eroded.geometry) return { polygon: null, areaM2: 0 }

    const erodedArea = area(eroded)
    if (!(erodedArea > 0)) return { polygon: null, areaM2: 0 }

    return {
      polygon: eroded.geometry as GeoJsonPolygonLike,
      areaM2: Math.round(erodedArea),
    }
  } catch {
    return { polygon: null, areaM2: 0 }
  }
}
